import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiCountry, ApiCustomField, ApiEnumGroup, ApiFunctionFull } from '@agunity/api-v4/build/data-contracts';
import { RootState } from '../store';
import { apiService } from '../../api/apiService';

export interface FuncAndRoleState { functionId: number, id: number, name: string }

export interface MemberState {
  isDirty_Member_Personal: boolean;
  member_Redirect_URL: string;
  isOpenUnsavedModal: boolean;
  isOpenSupportModal: boolean;
  tagList: ApiCustomField[];
  countryOptions: ApiCountry[] | [];
  functionStructureList: ApiFunctionFull[];
  isOpenViewAvatar: boolean;
  enumList: ApiEnumGroup[];
  addNewOrgInfo: { name: string, id: string };
  isToastOpen_navFromAddMember: boolean;
  nameCheck: { open: boolean, message: string };
  emailExists: boolean;
  addNewMemberFuncAndRoles: FuncAndRoleState | undefined;
  allRolesUnderFunc: (FuncAndRoleState | undefined)[];
  selectedRoles: number[];
  editMemberFullName: string;
}

const initialState: MemberState = {
  isDirty_Member_Personal: false,
  member_Redirect_URL: '',
  isOpenUnsavedModal: false,
  isOpenSupportModal: false,
  tagList: [],
  countryOptions: [],
  functionStructureList: [],
  isOpenViewAvatar: false,
  enumList: [],
  addNewOrgInfo: { name: '', id: '' },
  isToastOpen_navFromAddMember: false,
  nameCheck: { open: false, message: '' },
  emailExists: false,
  addNewMemberFuncAndRoles: undefined,
  allRolesUnderFunc: [],
  selectedRoles: [],
  editMemberFullName: ''
};

export const MemberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    setIsDirty_Member_Personal: (
      state: MemberState,
      action: PayloadAction<boolean>
    ) => {
      state.isDirty_Member_Personal = action.payload;
    },
    setMember_Redirect_URL: (
      state: MemberState,
      action: PayloadAction<string>
    ) => {
      state.member_Redirect_URL = action.payload;
    },
    setIsOpenUnsavedModal: (
      state: MemberState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenUnsavedModal = action.payload;
    },
    setIsOpenSupportModal: (
      state: MemberState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenSupportModal = action.payload;
    },
    setMember_TagList: (
      state: MemberState,
      action: PayloadAction<ApiCustomField[]>
    ) => {
      state.tagList = action.payload;
    },
    setMember_CountryOptions: (
      state: MemberState,
      action: PayloadAction<ApiCountry[] | []>
    ) => {
      state.countryOptions = action.payload;
    },
    setFunctionStructureList: (
      state: MemberState,
      action: PayloadAction<ApiFunctionFull[]>
    ) => {
      state.functionStructureList = action.payload;
    },
    setIsOpenViewAvatar: (
      state: MemberState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenViewAvatar = action.payload;
    },
    setMember_EnumList: (
      state: MemberState,
      action: PayloadAction<ApiEnumGroup[]>
    ) => {
      state.enumList = action.payload;
    },
    setAddNewOrgInfo: (
      state: MemberState,
      action: PayloadAction<{ name: string, id: string }>
    ) => {
      state.addNewOrgInfo = action.payload;
    },
    setIsToastOpen_navFromAddMember: (
      state: MemberState,
      action: PayloadAction<boolean>
    ) => {
      state.isToastOpen_navFromAddMember = action.payload;
    },
    setNameCheck: (
      state: MemberState,
      action: PayloadAction<{ open: boolean, message: string }>
    ) => {
      state.nameCheck = action.payload;
    },
    setEmailExists: (
      state: MemberState,
      action: PayloadAction<boolean>
    ) => {
      state.emailExists = action.payload;
    },
    setAddNewMemberFuncAndRoles: (
      state: MemberState,
      action: PayloadAction<FuncAndRoleState | undefined>
    ) => {
      state.addNewMemberFuncAndRoles = action.payload;
    },
    setAllRolesUnderFunc: (
      state: MemberState,
      action: PayloadAction<(FuncAndRoleState | undefined)[]>
    ) => {
      state.allRolesUnderFunc = action.payload;
    },
    setSelectedRoles: (
      state: MemberState,
      action: PayloadAction<number[]>
    ) => {
      state.selectedRoles = action.payload;
    },
    setEditMemberFullName: (
      state: MemberState,
      action: PayloadAction<string>
    ) => {
      state.editMemberFullName = action.payload;
    },
    clearMemberCache: (
      state: MemberState,
    ) => {
      Object.assign(state, initialState);
    },
  }
});

export const {
  setIsDirty_Member_Personal,
  setMember_Redirect_URL,
  setIsOpenUnsavedModal,
  setIsOpenSupportModal,
  setMember_TagList,
  setMember_CountryOptions,
  setFunctionStructureList,
  setIsOpenViewAvatar,
  setMember_EnumList,
  setAddNewOrgInfo,
  setIsToastOpen_navFromAddMember,
  setNameCheck,
  setEmailExists,
  setAddNewMemberFuncAndRoles,
  setAllRolesUnderFunc,
  setSelectedRoles,
  setEditMemberFullName,
  clearMemberCache
} = MemberSlice.actions;

export const selectIsOpenViewAvatar = (state: RootState) => state.member.isOpenViewAvatar;

export const fetchMemberBulkUploadTemplate = (orgId: number, existing: boolean) => (async () => {
  const response = await apiService.memberBulkV10UploadFileDetail(orgId, existing);
  return response;
});

export const memberBulkUpload = (data: FormData) => (async () => {
  const response = await apiService.memberBulkV10UploadCreate(data as any);
  return response;
});

export const fetchMemberBulkUploadDetails = (key: string) => (async () => {
  const response = await apiService.memberBulkV10UploadDetail(key);
  return response;
});

export const memberBulkUploadSubmit = (key: string) => (async () => {
  const response = await apiService.memberBulkV10CommitCreate(key);
  return response;
});

export const memberBulkUploadAbandonDelete = (key: string) => (async () => {
  const response = await apiService.memberBulkV10AbandonDelete(key);
  return response;
});

export default MemberSlice.reducer;
